import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";

// axios returns { data }; tolerate already-unwrapped bodies too.
function unwrap(res) {
  if (res && Object.prototype.hasOwnProperty.call(res, "data") &&
      !Object.prototype.hasOwnProperty.call(res, "review_token") &&
      !Object.prototype.hasOwnProperty.call(res, "ok")) return res.data;
  return res;
}
function show(v) { return v === null || v === undefined || v === "" ? "Not set" : String(v); }
function clientLabel(c) {
  return [c.name || "Unnamed member", c.age !== null && c.age !== undefined && c.age !== "" ? `age ${c.age}` : null, c.relationship || null]
    .filter(Boolean).join(" · ");
}
const FIELDS = [["name", "Name"], ["age", "Age"], ["gender", "Gender"], ["relationship", "Relationship"], ["category", "Category"]];

export default function SharingTreeRosterReview({ householdId, onApplied, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [choices, setChoices] = useState({});
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false);

  async function load() {
    setLoading(true); setError(""); setConflict(false); setChoices({}); setReview(null);
    try {
      const body = unwrap(await api.get(`/sharing-tree/households/${householdId}/roster-review`));
      setReview({
        review_token: body?.review_token,
        registered_members: Array.isArray(body?.registered_members) ? body.registered_members : [],
        client_members: Array.isArray(body?.client_members) ? body.client_members : [],
        year: body?.year,
      });
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }

  function toggle() {
    if (open) { setOpen(false); return; }
    setOpen(true); load();
  }

  const matches = Object.entries(choices).filter(([, v]) => v)
    .map(([member_id, profile_member_id]) => ({ member_id, profile_member_id }));

  async function apply() {
    if (!review || matches.length === 0 || pending || disabled || error) return;
    setPending(true); setError(""); setConflict(false);
    try {
      unwrap(await api.post(`/sharing-tree/households/${householdId}/roster-review`, {
        review_token: review.review_token, matches,
      }));
      onApplied && onApplied();
    } catch (e) {
      setConflict(e?.response?.status === 409);
      setError(formatApiError(e));
    } finally { setPending(false); }
  }

  const clients = review?.client_members || [];
  const used = new Set(Object.values(choices).filter(Boolean));

  return (
    <div className="mb-3" data-testid="roster-review">
      <button type="button" onClick={toggle} disabled={disabled && !open} aria-expanded={open}
              aria-controls="roster-review-panel" data-testid="roster-review-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)", color: "var(--fb-primary)", background: "#FFF" }}>
        {open ? "Close roster review" : "Review client roster"}
      </button>
      {disabled && (
        <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
          Save your changes and reopen this household before reviewing the client roster.
        </p>
      )}
      {open && (
        <section id="roster-review-panel" aria-label="Client roster review" className="rounded-xl p-4 mt-2"
                 style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
          <p className="text-xs mb-3" style={{ color: "var(--fb-text-muted)" }}>
            Compare each registered member with the current client roster. Choose a client member only when you are sure they are the same person.
            Applying updates name, age, gender, relationship and category. Toy requests, clothing sizes and printed IDs are retained.
            Members are not added or removed. Only the current Sharing Tree year can be updated.
            {" "}Ages use the program year minus birth year, as on the signup form. Missing profile values keep the registered values.
          </p>
          {loading && (
            <div className="space-y-2" data-testid="roster-review-loading" aria-busy="true">
              {[0, 1].map(i => <div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: "var(--fb-border)" }} />)}
            </div>
          )}
          {error && (
            <div role="alert" className="text-xs rounded-lg p-3 mb-3 border" style={{ borderColor: "var(--fb-error)", color: "var(--fb-error)", background: "#FFF" }}>
              {conflict ? "The registration or client roster changed since this comparison loaded. " : ""}{error}
              <div className="mt-2">
                <button type="button" onClick={load} disabled={loading || pending} data-testid="roster-review-reload"
                        className="px-3 py-1 rounded-lg text-xs font-semibold border disabled:opacity-50"
                        style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)", background: "#FFF" }}>
                  Reload comparison
                </button>
                <span className="ml-2" style={{ color: "var(--fb-text-muted)" }}>Reloading clears your selections.</span>
              </div>
            </div>
          )}
          {!loading && review && review.registered_members.length === 0 && (
            <div className="text-xs py-4 px-3 rounded-lg border border-dashed text-center" style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
              No registered members to review.
            </div>
          )}
          {!loading && review && review.registered_members.length > 0 && clients.length === 0 && (
            <p className="text-xs mb-2" style={{ color: "var(--fb-text-muted)" }}>The client profile has no current members to compare.</p>
          )}
          {!loading && review && review.registered_members.length > 0 && (
            <div className="space-y-3">
              {review.registered_members.map(m => {
                const sel = choices[m.id] || "";
                const src = clients.find(c => c.id === sel);
                const proposed = src && Object.fromEntries(FIELDS.map(([k]) =>
                  [k, src[k] === null || src[k] === undefined || src[k] === "" ? m[k] : src[k]]));
                const selId = `rr-${m.id}`;
                const catChange = proposed && show(proposed.category) !== show(m.category);
                return (
                  <fieldset key={m.id} className="rounded-lg border p-3 bg-white" style={{ borderColor: "var(--fb-border)" }} data-testid={`rr-row-${m.id}`}>
                    <legend className="px-1 text-xs font-semibold">
                      {m.printed_id ? <span className="font-mono mr-1">#{m.printed_id}</span> : null}{m.name || "Unnamed member"}
                    </legend>
                    <label htmlFor={selId} className="text-xs font-semibold mb-1 block" style={{ color: "var(--fb-text-muted)" }}>
                      Client roster source for {m.name || m.printed_id || "this member"}
                    </label>
                    <select id={selId} value={sel} disabled={pending || disabled || !!error}
                            onChange={e => setChoices({ ...choices, [m.id]: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:border-[#052F66] disabled:opacity-60"
                            style={{ borderColor: "var(--fb-border)", background: "#FFF" }}>
                      <option value="">Keep registration unchanged</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id} disabled={!c.name?.trim() || (used.has(c.id) && c.id !== sel)}>
                          {clientLabel(c)}{c.id === m.profile_member_id ? " (currently linked)" : ""}
                        </option>
                      ))}
                    </select>
                    {src && (
                      <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs" data-testid={`rr-compare-${m.id}`}>
                          <thead><tr style={{ color: "var(--fb-text-muted)" }}>
                            <th scope="col" className="text-left font-semibold py-1 pr-2">Field</th>
                            <th scope="col" className="text-left font-semibold py-1 pr-2">Current</th>
                            <th scope="col" className="text-left font-semibold py-1">Proposed</th>
                          </tr></thead>
                          <tbody>
                            {FIELDS.map(([k, label]) => {
                              const changed = show(m[k]) !== show(proposed[k]);
                              return (
                                <tr key={k} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                                  <th scope="row" className="text-left font-medium py-1 pr-2">{label}</th>
                                  <td className="py-1 pr-2">{show(m[k])}</td>
                                  <td className="py-1" style={changed ? { fontWeight: 600, color: "var(--fb-primary)" } : undefined}>
                                    {show(proposed[k])}{changed ? " (changes)" : ""}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {catChange && (
                          <p className="text-xs mt-2 rounded-md p-2" role="note" style={{ background: "#FEF3C7", color: "#92400E" }}>
                            Category changes from {show(m.category)} to {show(proposed.category)}. Report counts will change; saved toy requests and sizes remain stored.
                          </p>
                        )}
                      </div>
                    )}
                  </fieldset>
                );
              })}
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>{matches.length} selected</span>
                <button type="button" onClick={apply} disabled={matches.length === 0 || loading || pending || disabled || !!error} data-testid="roster-review-apply"
                        className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                        style={{ background: "var(--fb-primary)" }}>
                  {pending ? "Applying..." : "Apply selected updates"}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
